import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ICON: Record<string, string> = {
  factuur: '📄', dekking_verleend: '🛡️', dekking_ingetrokken: '⚠️',
  rating_gewijzigd: '📊', schade: '📁',
};

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/notifications`, { headers });
      setItems(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const markRead = async (id: string) => {
    await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, { method: 'POST', headers });
    load();
  };
  const markAll = async () => {
    await fetch(`${API_BASE_URL}/api/notifications/read-all`, { method: 'POST', headers });
    load();
  };

  const fmt = (d: string) => new Date(d).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Berichten</h1>
          <p className="text-sm text-slate-500">Notificaties over dekking, facturen, ratings en schades.</p>
        </div>
        <button onClick={markAll} className="text-sm font-medium text-mfb-blue border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50">
          Alles als gelezen
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen berichten.</div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.id}
              className={`flex items-start gap-4 rounded-xl border p-4 ${n.gelezen ? 'bg-white border-slate-200' : 'bg-mfb-blue/5 border-mfb-blue/20'}`}>
              <div className="text-2xl">{ICON[n.type] || '🔔'}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm ${n.gelezen ? 'font-medium text-slate-800' : 'font-semibold text-mfb-blue'}`}>{n.titel}</p>
                  {!n.gelezen && <span className="w-2 h-2 rounded-full bg-mfb-orange" />}
                  {(n.kanaal === 'mail' || n.kanaal === 'beide') && (
                    <span className="text-xs text-slate-400">📧 ook per e-mail</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-0.5">{n.bericht}</p>
                <p className="text-xs text-slate-400 mt-1">{fmt(n.created_at)}</p>
              </div>
              {!n.gelezen && (
                <button onClick={() => markRead(n.id)} className="text-xs text-mfb-blue hover:underline whitespace-nowrap">
                  Markeer gelezen
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default NotificationsPage;
